import {EventBus, HasEvents} from '@mintdata-wsp/common/bus/EventBus';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    ExtendSerieDirection,
    ExtendSerieType,
    FormulasExtendSerie,
    IExtendData,
    IExtendSerie,
    NumbersExtendSerie
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ExtendSerie';
import {MeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {AnySlot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {SlotUtil} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotUtil';
import {IWSPMeshContext} from '@mintdata-wsp/wsp/controllers/visualDesigner/spreadsheet/contexts/MeshContext';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {action} from 'mobx';

type MeshSelectionListener = {
    onSheetSelectionChanged:() => void;
}

export class MeshSelectionController implements HasEvents<MeshSelectionListener>
{
    @action
    public startRangeSelection(ctx:IWSPMeshContext, location:MeshLocation):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( !location )
            return;

        meshState.details.selectionRangeFrom = location;
        meshState.details.selectionRangeTo = location;
        meshState.details.isSelectRangeMode = true;

        EventBus.fire(this, 'sheetSelectionChanged');
    }

    @action
    public finishRangeSelection(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        meshState.details.isSelectRangeMode = false;

        const {cellSettingsBar} = rootState.ui.visualDesigner.workbench;

        if( cellSettingsBar.isPaintFormatMode )
        {
            controllers.ui.spreadsheets.cellSettingsBar.applyPaintFormatCellSetting(ctx);
        }
    }

    @action
    public setSelectionRangeTo(ctx:IWSPMeshContext, locationTo:MeshLocation):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        meshState.details.selectionRangeTo = locationTo;

        EventBus.fire(this, 'sheetSelectionChanged');
    }

    @action
    public startRangeExtension(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        meshState.details.isExtendRangeMode = true;
    }

    @action
    public finishRangeExtension(ctx:IWSPMeshContext, isCopyMode:boolean):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        const {details: {isExtendRangeMode, extendedRange, selectedRange}} = meshState;

        if( isExtendRangeMode )
        {
            if( extendedRange
                && selectedRange
                && !extendedRange.isEqualTo(selectedRange) )
            {
                if( !meshState.details.isReadonly )
                {
                    this._copyCellSelectedValues(ctx, selectedRange, extendedRange, isCopyMode);
                }

                meshState.details.selectionRangeFrom = extendedRange.from;
                meshState.details.selectionRangeTo = extendedRange.to;

                EventBus.fire(this, 'sheetSelectionChanged');
            }

            meshState.details.extendRangeTo = null;
            meshState.details.isExtendRangeMode = false;
        }
    }

    @action
    public setExtendRangeTo(ctx:IWSPMeshContext, locationTo:MeshLocation):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);
        meshState.details.extendRangeTo = locationTo;
    }

    private _copyCellSelectedValues(ctx:IWSPMeshContext, selectedRange:MeshRange, extendedRange:MeshRange, isCopyMode:boolean):void
    {
        const {
            from: {
                colIndex: colIndexFrom,
                rowIndex: rowIndexFrom
            },
            to: {
                colIndex: colIndexTo,
                rowIndex: rowIndexTo
            }
        } = selectedRange;

        const {
            from: {
                colIndex: extendedColIndexFrom,
                rowIndex: extendedRowIndexFrom
            },
            to: {
                colIndex: extendedColIndexTo,
                rowIndex: extendedRowIndexTo
            }
        } = extendedRange;

        if( extendedColIndexFrom < colIndexFrom )
        {
            this._extendNegative(
                ctx,
                selectedRange,
                extendedRange,
                isCopyMode,
                true
            );
        }
        else if( extendedColIndexTo > colIndexTo )
        {
            this._extendPositive(
                ctx,
                selectedRange,
                extendedRange,
                isCopyMode,
                true
            );
        }
        else if( extendedRowIndexFrom < rowIndexFrom )
        {
            this._extendNegative(
                ctx,
                selectedRange,
                extendedRange,
                isCopyMode
            );
        }
        else if( extendedRowIndexTo > rowIndexTo )
        {
            this._extendPositive(
                ctx,
                selectedRange,
                extendedRange,
                isCopyMode
            );
        }
    }

    private _extendNegative(ctx:IWSPMeshContext,
                            selectedRange:MeshRange,
                            extendedRange:MeshRange,
                            isCopyMode:boolean,
                            isChangeDirection:boolean = false):void
    {
        const {sheetId, sheetCtrl, sheetSettingsCtrl} = ctx;

        let {
            from: {
                colIndex: mainIndexFrom,
                rowIndex: crossIndexFrom
            },
            to: {
                colIndex: mainIndexTo,
                rowIndex: crossIndexTo
            }
        } = selectedRange;

        let {
            from: {
                colIndex: extendedMainIndexFrom,
                rowIndex: extendedCrossIndexFrom
            }
        } = extendedRange;

        if( isChangeDirection )
        {
            [mainIndexFrom, crossIndexFrom] = [crossIndexFrom, mainIndexFrom];
            [mainIndexTo, crossIndexTo] = [crossIndexTo, mainIndexTo];
            [extendedMainIndexFrom, extendedCrossIndexFrom] = [extendedCrossIndexFrom, extendedMainIndexFrom];
        }

        const extendData:IExtendData = this._getExtendData(
            ctx,
            selectedRange,
            isCopyMode,
            isChangeDirection
        );

        const locations:Array<SlotLocation> = [];
        const formulas:Array<SlotFormula> = [];
        const formats:Array<string> = [];

        for( let mainIndex:number = mainIndexFrom; mainIndex <= mainIndexTo; mainIndex++ )
        {
            const dataIndex:number = mainIndex - mainIndexFrom;
            const series:Array<IExtendSerie> = extendData.series[dataIndex];

            let crossIndex:number = crossIndexFrom - 1;
            let serieIndex:number = series.length - 1;
            let processedValuesCount:number = 0;

            while( crossIndex >= extendedCrossIndexFrom )
            {
                const colIndex:number = isChangeDirection ? crossIndex : mainIndex;
                const rowIndex:number = isChangeDirection ? mainIndex : crossIndex;
                const serie:IExtendSerie = series[serieIndex];
                const nextSerieValue:SlotFormula = serie.calculateNextValue(
                    ExtendSerieDirection.Negative,
                    series.length === 1
                );
                const nextformatId:string = serie.getCellFormat(ExtendSerieDirection.Negative);

                locations.splice(0, 0, new SlotLocation(colIndex, rowIndex));
                formulas.splice(0, 0, nextSerieValue != null ? nextSerieValue : null);
                formats.splice(0, 0, nextformatId);

                const cellSettings:MeshCellSettings = serie.getCellSettings(
                    ExtendSerieDirection.Negative
                );

                sheetSettingsCtrl.setCellSettings(sheetId, new MeshLocation(colIndex, rowIndex).asKey(), cellSettings);

                crossIndex--;
                processedValuesCount++;

                if( processedValuesCount >= serie.size )
                {
                    processedValuesCount = 0;

                    if( serieIndex === 0 )
                        serieIndex = series.length - 1;
                    else
                        serieIndex--;
                }
            }
        }

        sheetCtrl.setSlotFormulas(sheetId, locations, formulas, formats);
    }

    private _extendPositive(ctx:IWSPMeshContext,
                            selectedRange:MeshRange,
                            extendedRange:MeshRange,
                            isCopyMode:boolean,
                            isChangeDirection:boolean = false):void
    {
        const {sheetId, sheetCtrl, sheetSettingsCtrl} = ctx;

        let {
            from: {
                colIndex: mainIndexFrom,
                rowIndex: crossIndexFrom
            },
            to: {
                colIndex: mainIndexTo,
                rowIndex: crossIndexTo
            }
        } = selectedRange;

        let {
            to: {
                colIndex: extendedMainIndexTo,
                rowIndex: extendedCrossIndexTo
            }
        } = extendedRange;

        if( isChangeDirection )
        {
            [mainIndexFrom, crossIndexFrom] = [crossIndexFrom, mainIndexFrom];
            [mainIndexTo, crossIndexTo] = [crossIndexTo, mainIndexTo];
            [extendedMainIndexTo, extendedCrossIndexTo] = [extendedCrossIndexTo, extendedMainIndexTo];
        }

        const extendData:IExtendData = this._getExtendData(
            ctx,
            selectedRange,
            isCopyMode,
            isChangeDirection
        );

        const locations:Array<SlotLocation> = [];
        const formulas:Array<SlotFormula> = [];
        const formats:Array<string> = [];

        for( let mainIndex:number = mainIndexFrom; mainIndex <= mainIndexTo; mainIndex++ )
        {
            const dataIndex:number = mainIndex - mainIndexFrom;
            const series:Array<IExtendSerie> = extendData.series[dataIndex];

            let crossIndex:number = crossIndexTo + 1;
            let serieIndex:number = 0;
            let processedValuesCount:number = 0;

            while( crossIndex <= extendedCrossIndexTo )
            {
                const colIndex:number = isChangeDirection ? crossIndex : mainIndex;
                const rowIndex:number = isChangeDirection ? mainIndex : crossIndex;
                const serie:IExtendSerie = series[serieIndex];
                const nextSerieValue:SlotFormula = serie.calculateNextValue(
                    ExtendSerieDirection.Positive,
                    series.length === 1
                );
                const nextformatId:string = serie.getCellFormat(ExtendSerieDirection.Positive);

                locations.push(new SlotLocation(colIndex, rowIndex));
                formulas.push(nextSerieValue != null ? nextSerieValue : null);
                formats.push(nextformatId);

                const cellSettings:MeshCellSettings = serie.getCellSettings(
                    ExtendSerieDirection.Positive
                );

                sheetSettingsCtrl.setCellSettings(sheetId, new MeshLocation(colIndex, rowIndex).asKey(), cellSettings);

                crossIndex++;
                processedValuesCount++;

                if( processedValuesCount >= serie.size )
                {
                    processedValuesCount = 0;

                    if( serieIndex >= (series.length - 1) )
                        serieIndex = 0;
                    else
                        serieIndex++;
                }
            }
        }

        sheetCtrl.setSlotFormulas(sheetId, locations, formulas, formats);
    }

    private _getExtendData(ctx:IWSPMeshContext,
                           selectedRange:MeshRange,
                           isCopyMode:boolean,
                           isChangeDirection:boolean = false):IExtendData
    {
        const {sheetId, sheetCtrl, sheetSettingsCtrl} = ctx;

        let {
            from: {
                colIndex: mainIndexFrom,
                rowIndex: crossIndexFrom
            },
            to: {
                colIndex: mainIndexTo,
                rowIndex: crossIndexTo
            }
        } = selectedRange;

        if( isChangeDirection )
        {
            [mainIndexFrom, crossIndexFrom] = [crossIndexFrom, mainIndexFrom];
            [mainIndexTo, crossIndexTo] = [crossIndexTo, mainIndexTo];
        }

        const extendData:IExtendData = {
            series: []
        };

        for( let mainIndex:number = mainIndexFrom; mainIndex <= mainIndexTo; mainIndex++ )
        {
            const series:Array<IExtendSerie> = [];

            extendData.series.push(series);

            let prevSerieType:ExtendSerieType = null;
            let currentSerie:IExtendSerie = null;

            for( let crossIndex:number = crossIndexFrom; crossIndex <= crossIndexTo; crossIndex++ )
            {
                const cellColIndex = isChangeDirection
                    ? crossIndex
                    : mainIndex;

                const cellRowIndex = isChangeDirection
                    ? mainIndex
                    : crossIndex;

                const slotLocation = new SlotLocation(cellColIndex, cellRowIndex);
                const slot:AnySlot = sheetCtrl.findSlot(sheetId, slotLocation);

                let cellSettings = sheetSettingsCtrl.getCellSettings(
                    sheetId,
                    new MeshLocation(cellColIndex, cellRowIndex).asKey()
                );

                let currentSerieType:ExtendSerieType = ExtendSerieType.Formula;
                let knownValue:any = null;
                let formatId:string = sheetSettingsCtrl.getCellFormat(sheetId, slotLocation.toKey()).id;

                if( slot && slot.formula )
                {
                    if( !isCopyMode && (slot.isSimpleValue || SlotUtil.isIValue(slot.formula)) )
                    {
                        const integerValue:number = SlotUtil.getIntegerValue(slot);
                        const decimalValue:number = SlotUtil.getDecimalValue(slot);

                        if( integerValue != null || decimalValue != null )
                        {
                            currentSerieType = ExtendSerieType.Number;
                            knownValue = integerValue != null ? integerValue : decimalValue;
                        }
                        else
                            knownValue = slot.formula;
                    }
                    else
                        knownValue = slot.formula;
                }

                if( !currentSerie || currentSerieType !== prevSerieType )
                {
                    prevSerieType = currentSerieType;

                    switch( currentSerieType )
                    {
                        case ExtendSerieType.Number:
                            currentSerie = new NumbersExtendSerie();
                            break;
                        default:
                            currentSerie = new FormulasExtendSerie();
                    }

                    series.push(currentSerie);
                }

                currentSerie.addKnownValue(knownValue);
                currentSerie.addCellSettings(cellSettings);
                currentSerie.addCellFormat(formatId);
            }
        }

        return extendData;
    }

    @action
    public setCellAtCursor(ctx:IWSPMeshContext, location:MeshLocation):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( location )
        {
            meshState.details.cellAtCursor = location;
        }
    }

    @action
    public selectCell(ctx:IWSPMeshContext, location:MeshLocation):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( location )
        {
            const newLocation = new MeshLocation(location.colIndex || 0, location.rowIndex || 0);

            meshState.details.activeCell = newLocation;
            meshState.details.selectionRangeFrom = location;
            meshState.details.selectionRangeTo = location;

            mesh.closeNoteIfSelectCell(ctx, newLocation);

            EventBus.fire(this, 'sheetSelectionChanged');
        }
    }

    @action
    public selectCellRange(ctx:IWSPMeshContext, locationFrom:MeshLocation, locationTo:MeshLocation):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( location )
        {
            meshState.details.activeCell = new MeshLocation(locationFrom.colIndex || 0, locationFrom.rowIndex || 0);
            meshState.details.selectionRangeFrom = locationFrom;
            meshState.details.selectionRangeTo = locationTo;

            EventBus.fire(this, 'sheetSelectionChanged');
        }
    }

    @action
    public selectCellAtXY(ctx:IWSPMeshContext, x:number, y:number):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const foundActiveRow = this._getIndexFromCoordinate(y, meshState.rowHeights);
        const foundActiveCol = this._getIndexFromCoordinate(x, meshState.colWidths);

        if( foundActiveCol !== -1 && foundActiveRow !== -1 )
        {
            meshState.details.activeCell = new MeshLocation(foundActiveCol, foundActiveRow);
            EventBus.fire(this, 'sheetSelectionChanged');
        }
    }

    public getCellAtXY(ctx:IWSPMeshContext, x:number, y:number):MeshLocation
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const foundActiveRow = this._getIndexFromCoordinate(y, meshState.rowHeights);
        const foundActiveCol = this._getIndexFromCoordinate(x, meshState.colWidths);

        if( foundActiveCol === -1 || foundActiveRow === -1 )
            return null;

        return new MeshLocation(foundActiveCol, foundActiveRow);
    }

    private _getIndexFromCoordinate(coordinate:number, sizes:number[]):number
    {
        let startCoord = 0;
        let endCoord = 0;

        for( let i = 0; i < sizes.length; i++ )
        {
            endCoord += sizes[i];

            if( coordinate >= startCoord && coordinate <= endCoord )
            {
                return i;
            }

            startCoord += sizes[i];
        }

        return -1;
    }

    @action
    public checkActiveCellCol(ctx:IWSPMeshContext, newColIndex:number):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {visibleStartColIndex, visibleEndColIndex, colWidths, alignedScrollLeft, details: {visibleWidth}} = meshState;
        let newActiveCellScrollPosLeft:number = 0;

        for( let i = 0; i < newColIndex; i++ )
            newActiveCellScrollPosLeft += colWidths[i];

        if( newColIndex < visibleStartColIndex )
            meshState.details.scrollLeft = newActiveCellScrollPosLeft;
        else if( newColIndex >= visibleEndColIndex )
        {
            const newActiveCellWidth:number = colWidths[newColIndex];

            if( newActiveCellWidth >= visibleWidth )
            {
                meshState.details.scrollLeft = newActiveCellScrollPosLeft;
                return;
            }

            if( newColIndex === visibleEndColIndex )
            {
                // right edge of the cell is visible
                if( newActiveCellScrollPosLeft + colWidths[newColIndex] - alignedScrollLeft < visibleWidth )
                    return;

                let scrollDelta:number = 0;
                let i:number = visibleStartColIndex;

                while( scrollDelta < newActiveCellWidth && i < newColIndex )
                {
                    scrollDelta += colWidths[i++];
                }

                meshState.details.scrollLeft += scrollDelta;
            }
            else
            {
                let widthOfVisibleColumns:number = 0;
                let i:number = newColIndex;

                for( i; widthOfVisibleColumns < visibleWidth; i-- )
                {
                    widthOfVisibleColumns += colWidths[i];
                }

                // in computed meshState.visibleStartColIndex for newActiveCellScrollPosLeft
                // index will be newColIndex - 1, so we need to add at least 1px to have needed index
                meshState.details.scrollLeft = newActiveCellScrollPosLeft + 1 -
                    (widthOfVisibleColumns - colWidths[i + 1] - colWidths[newColIndex]);
            }
        }
    }

    @action
    public checkActiveCellRow(ctx:IWSPMeshContext, newRowIndex:number):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {
            visibleStartRowIndex,
            visibleEndRowIndex,
            rowHeights,
            alignedScrollTop,
            details: {visibleHeight}
        } = meshState;
        let newActiveCellScrollPosTop:number = 0;

        for( let i = 0; i < newRowIndex; i++ )
            newActiveCellScrollPosTop += rowHeights[i];

        if( newRowIndex < visibleStartRowIndex )
            meshState.details.scrollTop = newActiveCellScrollPosTop;
        else if( newRowIndex >= visibleEndRowIndex )
        {
            const newActiveCellHeight:number = rowHeights[newRowIndex];

            if( newActiveCellHeight >= visibleHeight )
            {
                meshState.details.scrollTop = newActiveCellScrollPosTop;
                return;
            }

            if( newRowIndex === visibleEndRowIndex )
            {
                // top edge of the cell is visible
                if( newActiveCellScrollPosTop + rowHeights[newRowIndex] - alignedScrollTop < visibleHeight )
                    return;

                let scrollDelta:number = 0;
                let i:number = visibleStartRowIndex;

                while( scrollDelta < newActiveCellHeight && i < newRowIndex )
                {
                    scrollDelta += rowHeights[i++];
                }

                meshState.details.scrollTop += scrollDelta;
            }
            else
            {
                let heightOfVisibleRows:number = 0;
                let i:number = newRowIndex;

                for( i; heightOfVisibleRows < visibleHeight; i-- )
                {
                    heightOfVisibleRows += rowHeights[i];
                }

                // in computed meshState.visibleStartRowIndex for newActiveCellScrollPosTop
                // index will be newRowIndex - 1, so we need to add at least 1px to have needed index
                meshState.details.scrollTop = newActiveCellScrollPosTop + 1 -
                    (heightOfVisibleRows - rowHeights[i + 1] - rowHeights[newRowIndex]);
            }
        }
    }

    @action
    public moveActiveCellLeft(ctx:IWSPMeshContext, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {
            details: {
                activeCell: {
                    colIndex
                }
            }
        } = meshState;

        if( colIndex > 0 )
        {
            meshState.details.activeCell = meshState.details.activeCell.makeCopyAndMove(-1, 0);
            meshState.details.selectionRangeFrom = meshState.details.activeCell;
            meshState.details.selectionRangeTo = meshState.details.activeCell;
        }

        EventBus.fire(this, 'sheetSelectionChanged');

        this.checkActiveCellCol(ctx, meshState.details.activeCell.colIndex);
        this.checkActiveCellRow(ctx, meshState.details.activeCell.rowIndex);
    }

    @action
    public moveActiveCellUp(ctx:IWSPMeshContext, isCheckRange:boolean = false, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const {
            details: {
                activeCell: {
                    colIndex,
                    rowIndex
                }
            }
        } = meshState;

        if( isCheckRange && meshState.details.selectedRange && !meshState.details.selectedRange.isSingleCell )
        {
            const {
                details: {
                    selectedRange: {
                        from: {
                            colIndex: colIndexFrom,
                            rowIndex: rowIndexFrom
                        },
                        to: {
                            colIndex: colIndexTo,
                            rowIndex: rowIndexTo
                        }
                    }
                }
            } = meshState;

            meshState.details.activeCell = new MeshLocation(
                rowIndex > rowIndexFrom
                    ? colIndex
                    : colIndex > colIndexFrom
                    ? colIndex - 1
                    : colIndexTo,
                rowIndex > rowIndexFrom
                    ? (rowIndex - 1)
                    : rowIndexTo
            );
        }
        else if( rowIndex > 0 )
        {
            meshState.details.activeCell = meshState.details.activeCell.makeCopyAndMove(0, -1);
            meshState.details.selectionRangeFrom = meshState.details.activeCell;
            meshState.details.selectionRangeTo = meshState.details.activeCell;
        }

        EventBus.fire(this, 'sheetSelectionChanged');
        
        this.checkActiveCellCol(ctx, meshState.details.activeCell.colIndex);
        this.checkActiveCellRow(ctx, meshState.details.activeCell.rowIndex);
    }

    @action
    public moveActiveCellRight(ctx:IWSPMeshContext, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const size = meshState.size;
        const {
            details: {
                activeCell: {
                    colIndex,
                    rowIndex
                }
            }
        } = meshState;

        if( colIndex < size.numCols - 1 )
        {
            meshState.details.activeCell = meshState.details.activeCell.makeCopyAndMove(1, 0);
            meshState.details.selectionRangeFrom = meshState.details.activeCell;
            meshState.details.selectionRangeTo = meshState.details.activeCell;
        }

        EventBus.fire(this, 'sheetSelectionChanged');

        this.checkActiveCellCol(ctx, meshState.details.activeCell.colIndex);
        this.checkActiveCellRow(ctx, meshState.details.activeCell.rowIndex);
    }

    @action
    public moveActiveCellDown(ctx:IWSPMeshContext, isCheckRange:boolean = false, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        const size = meshState.size;
        const {
            details: {
                activeCell: {
                    colIndex,
                    rowIndex
                }
            }
        } = meshState;

        if( isCheckRange && meshState.details.selectedRange && !meshState.details.selectedRange.isSingleCell )
        {
            const {
                details: {
                    activeCell: {
                        colIndex,
                        rowIndex
                    },
                    selectedRange: {
                        from: {
                            colIndex: colIndexFrom,
                            rowIndex: rowIndexFrom
                        },
                        to: {
                            colIndex: colIndexTo,
                            rowIndex: rowIndexTo
                        }
                    }
                }
            } = meshState;

            meshState.details.activeCell = new MeshLocation(
                rowIndex < rowIndexTo
                    ? colIndex
                    : colIndex < colIndexTo
                    ? colIndex + 1
                    : colIndexFrom,
                rowIndex < rowIndexTo
                    ? (rowIndex + 1)
                    : rowIndexFrom
            );
        }
        else if( rowIndex < size.numRows - 1 )
        {
            meshState.details.activeCell = meshState.details.activeCell.makeCopyAndMove(0, 1);
            meshState.details.selectionRangeFrom = meshState.details.activeCell;
            meshState.details.selectionRangeTo = meshState.details.activeCell;
        }

        EventBus.fire(this, 'sheetSelectionChanged');

        this.checkActiveCellCol(ctx, meshState.details.activeCell.colIndex);
        this.checkActiveCellRow(ctx, meshState.details.activeCell.rowIndex);
    }

    @action
    public extendSelectionLeft(ctx:IWSPMeshContext, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( meshState.details.selectedRange )
        {
            const {
                details: {
                    selectedRange: {
                        from: {
                            colIndex: colIndexFrom
                        },
                        to: {
                            colIndex: colIndexTo
                        }
                    },
                    activeCell: {
                        colIndex
                    },
                    selectionRangeFrom,
                    selectionRangeTo
                },
                visibleStartColIndex,
            } = meshState;

            let newColIndex:number;

            if( colIndexTo > colIndexFrom && colIndexTo > colIndex )
            {
                newColIndex = colIndexTo - 1;

                if( selectionRangeTo.colIndex > selectionRangeFrom.colIndex )
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithCol(newColIndex);
                else
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithCol(newColIndex);
            }
            else if( colIndexFrom > 0 || (colIndexFrom === null && visibleStartColIndex > 0) )
            {
                newColIndex = colIndexFrom ? colIndexFrom - 1 : visibleStartColIndex - 1;

                if( selectionRangeFrom.colIndex < selectionRangeTo.colIndex )
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithCol(newColIndex);
                else
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithCol(newColIndex);
            }

            EventBus.fire(this, 'sheetSelectionChanged');

            this.checkActiveCellCol(ctx, newColIndex);
        }
    }

    @action
    public extendSelectionUp(ctx:IWSPMeshContext, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( meshState.details.selectedRange )
        {
            const {
                details: {
                    selectedRange: {
                        from: {
                            rowIndex: rowIndexFrom
                        },
                        to: {
                            rowIndex: rowIndexTo
                        }
                    },
                    activeCell: {
                        rowIndex
                    },
                    selectionRangeFrom,
                    selectionRangeTo
                },
                visibleStartRowIndex,
            } = meshState;

            let newRowIndex:number;

            if( rowIndexTo > rowIndexFrom && rowIndexTo > rowIndex )
            {
                newRowIndex = rowIndexTo - 1;

                if( selectionRangeTo.rowIndex > selectionRangeFrom.rowIndex )
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithRow(newRowIndex);
                else
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithRow(newRowIndex);
            }
            else if( rowIndexFrom > 0 || (rowIndexFrom === null && visibleStartRowIndex > 0) )
            {
                newRowIndex = rowIndexFrom ? rowIndexFrom - 1 : visibleStartRowIndex - 1;

                if( selectionRangeFrom.rowIndex < selectionRangeTo.rowIndex )
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithRow(newRowIndex);
                else
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithRow(newRowIndex);
            }

            EventBus.fire(this, 'sheetSelectionChanged');

            this.checkActiveCellRow(ctx, newRowIndex);
        }
    }

    @action
    public extendSelectionRight(ctx:IWSPMeshContext, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( meshState.details.selectedRange )
        {
            const {
                details: {
                    selectedRange: {
                        from: {
                            colIndex: colIndexFrom
                        },
                        to: {
                            colIndex: colIndexTo
                        }
                    },
                    activeCell: {
                        colIndex
                    },
                    selectionRangeFrom,
                    selectionRangeTo
                }
            } = meshState;

            let newColIndex:number;

            if( colIndexFrom < colIndexTo && colIndexFrom < colIndex )
            {
                newColIndex = colIndexFrom + 1;

                if( selectionRangeTo.colIndex < selectionRangeFrom.colIndex )
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithCol(newColIndex);
                else
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithCol(newColIndex);
            }
            else if( colIndexTo < meshState.size.numCols - 1 )
            {
                newColIndex = colIndexTo + 1;

                if( selectionRangeFrom.colIndex > selectionRangeTo.colIndex )
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithCol(newColIndex);
                else
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithCol(newColIndex);
            }

            EventBus.fire(this, 'sheetSelectionChanged');

            this.checkActiveCellCol(ctx, newColIndex);
        }
    }

    @action
    public extendSelectionDown(ctx:IWSPMeshContext, toImportantCell:boolean = false):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        if( meshState.details.selectedRange )
        {
            const {
                details: {
                    selectedRange: {
                        from: {
                            rowIndex: rowIndexFrom
                        },
                        to: {
                            rowIndex: rowIndexTo
                        }
                    },
                    activeCell: {
                        rowIndex
                    },
                    selectionRangeFrom,
                    selectionRangeTo
                }
            } = meshState;

            let newRowIndex:number;

            if( rowIndexFrom < rowIndexTo && rowIndexFrom < rowIndex )
            {
                newRowIndex = rowIndexFrom + 1;

                if( selectionRangeTo.rowIndex < selectionRangeFrom.rowIndex )
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithRow(newRowIndex);
                else
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithRow(newRowIndex);
            }
            else if( rowIndexTo < meshState.size.numRows - 1 )
            {
                newRowIndex = rowIndexTo + 1;

                if( selectionRangeFrom.rowIndex > selectionRangeTo.rowIndex )
                    meshState.details.selectionRangeFrom = selectionRangeFrom.makeCopyWithRow(newRowIndex);
                else
                    meshState.details.selectionRangeTo = selectionRangeTo.makeCopyWithRow(newRowIndex);
            }

            EventBus.fire(this, 'sheetSelectionChanged');

            this.checkActiveCellRow(ctx, newRowIndex);
        }
    }

    @action
    public extendSelectionToCell(ctx:IWSPMeshContext, location:MeshLocation):void
    {
        this.setSelectionRangeTo(ctx, location);

        EventBus.fire(this, 'sheetSelectionChanged');

        this.checkActiveCellCol(ctx, location.colIndex);
        this.checkActiveCellRow(ctx, location.rowIndex);
    }

    @action
    public extendExtendedRangeToCell(ctx:IWSPMeshContext, location:MeshLocation):void
    {
        this.setExtendRangeTo(ctx, location);
        
        EventBus.fire(this, 'sheetSelectionChanged');

        this.checkActiveCellCol(ctx, location.colIndex);
        this.checkActiveCellRow(ctx, location.rowIndex);
    }

    @action
    public selectAll(ctx:IWSPMeshContext):void
    {
        const {mesh} = controllers.ui.spreadsheets;
        const meshState = mesh.getState(ctx);

        meshState.details.selectionRangeFrom = new MeshLocation(0, 0);
        meshState.details.selectionRangeTo = new MeshLocation(meshState.size.numCols - 1, meshState.size.numRows - 1);

        EventBus.fire(this, 'sheetSelectionChanged');
    }
}
